import { memo, forwardRef, useMemo } from 'react';
import MuxPlayer from '@mux/mux-player-react';
import type { PulseContentItem, TipPopup } from '../types';
import { PulseOverlay } from './PulseOverlay';
import { PulseTipPopup } from './PulseTipPopup';

interface PulseCellProps {
  item: PulseContentItem;
  isPlaying?: boolean | undefined;
  tipPopups?: TipPopup[] | undefined;
  style?: React.CSSProperties | undefined;
}

/**
 * Individual tile: renders Mux HLS player for video or image for photos.
 * Memoized so grid reorders (position shuffles) don't cause media to reload.
 * The FLIP animation moves the DOM element — the player keeps playing uninterrupted.
 *
 * Videos use preload="metadata" and loop — once loaded, they play from cache.
 * The browser only fetches the stream once; subsequent loops serve from disk cache.
 */
export const PulseCell = memo(
  forwardRef<HTMLDivElement, PulseCellProps>(
    function PulseCell({ item, isPlaying, tipPopups, style }, ref) {
      const isPixelated = !item.moderationCleared && item.state !== 'moderated' && item.state !== 'deleted';
      const isDeleted = item.state === 'deleted';
      const isModerated = item.state === 'moderated';

      const mediaClass = useMemo(() => {
        const classes: string[] = [];
        if (isPixelated) classes.push('pulse-pixelate');
        if (isDeleted) classes.push('saturate-0 brightness-50');
        if (isModerated) classes.push('pulse-moderated-img');
        return classes.join(' ');
      }, [isPixelated, isDeleted, isModerated]);

      const borderClass = useMemo(() => {
        if (item.state === 'trending') return 'pulse-cell--trending';
        if (item.state === 'new') return 'pulse-cell--new';
        return '';
      }, [item.state]);

      const isVideo = !!item.playbackUrl;
      const showVideo = isVideo && isPlaying;

      return (
        <div
          ref={ref}
          className={`pulse-cell relative overflow-hidden ${borderClass}`}
          style={style}
          data-content-id={item.contentId}
        >
          {/* Media: Mux player (video) or image — fills cell with cover */}
          <div className={`absolute inset-0 bg-black ${mediaClass}`}>
            {showVideo ? (
              <MuxPlayer
                src={item.playbackUrl}
                poster={item.thumbnailUrl || undefined}
                autoPlay="muted"
                muted
                loop
                playsInline
                preload="metadata"
                style={{ width: '100%', height: '100%' }}
                // @ts-expect-error - mux-player custom attributes
                nohotkeys=""
                primaryColor="transparent"
                secondaryColor="transparent"
              />
            ) : item.thumbnailUrl ? (
              <img
                src={item.thumbnailUrl}
                alt=""
                className="size-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="size-full bg-[#1a1a2e]" />
            )}
          </div>

          {/* State overlays */}
          <PulseOverlay item={item} />

          {/* Tip popups */}
          {tipPopups?.map((popup, i) => (
            <PulseTipPopup key={`${popup.contentId}-${popup.createdAt}-${i}`} popup={popup} />
          ))}

          {/* Creator tag (bottom-left, subtle) */}
          {item.state !== 'deleted' && item.state !== 'moderated' && (
            <div className="absolute bottom-1 left-1 max-w-[80%] truncate rounded bg-black/50 px-1.5 py-0.5 text-[10px] text-white/70">
              @{item.creatorUsername}
            </div>
          )}
        </div>
      );
    },
  ),
  // Custom equality: only re-render if item data or tipPopups actually changed.
  // Grid reorders change position (handled by FLIP transforms on the DOM node)
  // but should NOT cause the media player to reload.
  (prev, next) => {
    return (
      prev.item.contentId === next.item.contentId &&
      prev.item.state === next.item.state &&
      prev.item.monetized === next.item.monetized &&
      prev.item.playbackUrl === next.item.playbackUrl &&
      prev.item.thumbnailUrl === next.item.thumbnailUrl &&
      prev.item.moderationCleared === next.item.moderationCleared &&
      prev.isPlaying === next.isPlaying &&
      prev.tipPopups === next.tipPopups &&
      prev.style?.gridRow === next.style?.gridRow &&
      prev.style?.gridColumn === next.style?.gridColumn
    );
  },
);
